import { FiltersMapper } from '../../../hooks/use-filters/useFilters';
import {
  AgentSerpPageFilterOption, AgentSerpSortOption, AgentSerpPageFilters,
  AgentSerpPageFilterKeys,
} from './types';
import { SortOrder } from '../../../providers/pagination';
import { JobTitle } from '../../../domain/types';

export const LOAD_SERP_AGENTS_ERROR_MESSAGE = 'Non è stato possibile recuperare gli agenti.';
export const NO_SERP_AGENTS = 'Non sono presenti agenti.';

export const ALL_AGENTS_FILTER_OPTION: AgentSerpPageFilterOption = {
  key: 'all',
  label: {
    short: 'Tutti',
    long: 'Tutti gli agenti',
  },
  value: {},
};

export const STATUS_FILTERS: AgentSerpPageFilterOption[] = [
  {
    key: 'active',
    label: {
      short: 'Attivi',
      long: 'Agenti attivi',
    },
    value: {
      active: true,
    },
  },
  {
    key: 'inactive',
    label: {
      short: 'Non attivi',
      long: 'Agenti non attivi',
    },
    value: {
      active: false,
    },
  },
];

export const AGENT_EXPERIENCE_FILTERS: AgentSerpPageFilterOption[] = [
  {
    key: 'rookie',
    label: {
      short: 'Rookie',
      long: 'Agenti rookie',
    },
    value: {
      rookie: true,
    },
  },
  {
    key: 'expert',
    label: {
      short: 'Esperti',
      long: 'Agenti esperti',
    },
    value: {
      rookie: false,
    },
  },
];

export const AGENT_JOB_TITLE_FILTERS: AgentSerpPageFilterOption[] = [
  {
    key: 'agent',
    label: {
      short: 'Agente',
      long: 'Agente',
    },
    value: {
      jobTitle: JobTitle.AGENTE,
    },
  },
  {
    key: 'areaManager',
    label: {
      short: 'Area Manager',
      long: 'Area Manager',
    },
    value: {
      jobTitle: JobTitle.AREA_MANAGER,
    },
  },
  {
    key: 'broker',
    label: {
      short: 'Broker',
      long: 'Broker',
    },
    value: {
      jobTitle: JobTitle.BROKER,
    },
  },
  {
    key: 'consultant',
    label: {
      short: 'Consulente',
      long: 'Consulente',
    },
    value: {
      jobTitle: JobTitle.CONSULENTE,
    },
  },
  {
    key: 'teamLeader',
    label: {
      short: 'Team Leader',
      long: 'Team Leader',
    },
    value: {
      jobTitle: JobTitle.TEAM_LEADER,
    },
  },
  {
    key: 'executive',
    label: {
      short: 'Executive',
      long: 'Executive',
    },
    value: {
      jobTitle: JobTitle.EXECUTIVE,
    },
  },
];

export const GROUPED_FILTER_OPTIONS = [
  {
    label: 'Stato',
    options: STATUS_FILTERS,
  },
  {
    label: 'Esperienza Dove',
    options: AGENT_EXPERIENCE_FILTERS,
  },
  {
    label: 'Ruolo',
    options: AGENT_JOB_TITLE_FILTERS,
  },
];

export const DEFAULT_FILTER_OPTION: AgentSerpPageFilterOption = {
  key: 'active',
  label: {
    short: 'Attivi',
    long: 'Agenti attivi',
  },
  value: {
    active: true,
  },
};

export const FILTER_OPTIONS: AgentSerpPageFilterOption[] = [
  ...STATUS_FILTERS,
  ...AGENT_EXPERIENCE_FILTERS,
  ...AGENT_JOB_TITLE_FILTERS,
  ALL_AGENTS_FILTER_OPTION,
];

export const DEFAULT_SORT_OPTION: AgentSerpSortOption = {
  key: 'default',
  label: 'In evidenza',
} as const;

export const MOST_RECENT_STARTED_SORT_OPTION: AgentSerpSortOption = {
  key: 'mostRecentAgentStarted',
  label: 'Attivazione più recente',
  value: {
    startDate: SortOrder.DESC,
  },
} as const;

export const LEAST_RECENT_STARTED_SORT_OPTION: AgentSerpSortOption = {
  key: 'leastRecentAgentStarted',
  label: 'Attivazione meno recente',
  value: {
    startDate: SortOrder.ASC,
  },
} as const;

export const HIGHEST_RANK_SORT_OPTION: AgentSerpSortOption = {
  key: 'highestRank',
  label: 'Classifica migliore',
  value: {
    'agentRanking.rank': SortOrder.ASC,
    startDate: SortOrder.DESC,
  },
};

export const LOWEST_RANK_SORT_OPTION: AgentSerpSortOption = {
  key: 'lowestRank',
  label: 'Classifica peggiore',
  value: {
    'agentRanking.rank': SortOrder.DESC,
    startDate: SortOrder.DESC,
  },
};

export const HIGHEST_GOAL_TARGET_SORT_OPTION: AgentSerpSortOption = {
  key: 'highestGoalTarget',
  label: 'Obiettivo più alto',
  value: {
    'goal.grossRevenue': SortOrder.DESC,
    startDate: SortOrder.DESC,
  },
};

export const LOWEST_GOAL_TARGET_SORT_OPTION: AgentSerpSortOption = {
  key: 'lowestGoalTarget',
  label: 'Obiettivo più basso',
  value: {
    'goal.grossRevenue': SortOrder.ASC,
    startDate: SortOrder.DESC,
  },
};

export const ALPHABETICAL_ASCENDING_SORT_OPTION: AgentSerpSortOption = {
  key: 'alphabeticalAsc',
  label: 'Alfabetico crescente',
  value: {
    surname: SortOrder.ASC,
    name: SortOrder.ASC,
  },
};

export const ALPHABETICAL_DESCENDING_SORT_OPTION: AgentSerpSortOption = {
  key: 'alphabeticalDesc',
  label: 'Alfabetico decrescente',
  value: {
    surname: SortOrder.DESC,
    name: SortOrder.DESC,
  },
};

export const SORT_OPTIONS_PER_FILTER: Record<AgentSerpPageFilterKeys, AgentSerpSortOption> = {
  active: {
    ...MOST_RECENT_STARTED_SORT_OPTION,
    key: 'default',
  },
  inactive: {
    label: 'Disattivazione più recente',
    value: {
      endDate: SortOrder.DESC,
    },
    key: 'default',
  },
  rookie: {
    ...MOST_RECENT_STARTED_SORT_OPTION,
    key: 'default',
  },
  expert: {
    ...MOST_RECENT_STARTED_SORT_OPTION,
    key: 'default',
  },
  agent: {
    ...MOST_RECENT_STARTED_SORT_OPTION,
    key: 'default',
  },
  areaManager: {
    ...LEAST_RECENT_STARTED_SORT_OPTION,
    key: 'default',
  },
  broker: {
    ...MOST_RECENT_STARTED_SORT_OPTION,
    key: 'default',
  },
  consultant: {
    ...MOST_RECENT_STARTED_SORT_OPTION,
    key: 'default',
  },
  executive: {
    ...MOST_RECENT_STARTED_SORT_OPTION,
    key: 'default',
  },
  teamLeader: {
    ...MOST_RECENT_STARTED_SORT_OPTION,
    key: 'default',
  },
  all: {
    ...MOST_RECENT_STARTED_SORT_OPTION,
    key: 'default',
  },
};

export const SORT_OPTIONS: AgentSerpSortOption[] = [
  DEFAULT_SORT_OPTION,
  MOST_RECENT_STARTED_SORT_OPTION,
  LEAST_RECENT_STARTED_SORT_OPTION,
  HIGHEST_RANK_SORT_OPTION,
  LOWEST_RANK_SORT_OPTION,
  HIGHEST_GOAL_TARGET_SORT_OPTION,
  LOWEST_GOAL_TARGET_SORT_OPTION,
  ALPHABETICAL_ASCENDING_SORT_OPTION,
  ALPHABETICAL_DESCENDING_SORT_OPTION,
] as const;

export const FILTERS_MAPPER: FiltersMapper<AgentSerpPageFilters> = {
  q: (v) => v,
  filter: (v) => FILTER_OPTIONS.find(({
    key,
  }) => key === v)?.key || DEFAULT_FILTER_OPTION.key,
  sort: (v) => SORT_OPTIONS.find(({
    key,
  }) => key === v)?.key || DEFAULT_SORT_OPTION.key,
};
